<script lang="ts">
	import type { CollectionPreview } from '$lib/features/collection/model/collectionPreview';
	import { UrlUtil, type NavLink } from '$lib/utils/url.util';
	import IconArrowRight from '~icons/mdi/arrow-right';

	const {
		collection,
		index,
		nav,
		loading = 'lazy',
		fetchPriority = 'auto'
	}: {
		collection: CollectionPreview;
		index: number;
		nav: NavLink;
		loading?: 'eager' | 'lazy';
		fetchPriority?: 'high' | 'auto';
	} = $props();

	const ariaLabelledBy = $derived<string>(`collection-title-${collection.metadata.slug}`);

	function plateNumber(index: number): string {
		return String(index + 1).padStart(2, '0');
	}

	function mosaicClass(count: number, index: number): string {
		if (count <= 1) return 'col-span-2 row-span-2';
		if (count === 2) return 'col-span-1 row-span-2';
		if (count === 3) return index === 0 ? 'col-span-1 row-span-2' : 'col-span-1 row-span-1';
		return 'col-span-1 row-span-1';
	}
</script>

<div
	class="group card h-full overflow-hidden rounded-box border-2 border-base-300/60 bg-base-200 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-base-300 hover:bg-base-300 hover:shadow-md"
>
	<a
		class="contents"
		href="/{nav.href}"
		data-sveltekit-preload-data={nav.preloadData}
		data-sveltekit-preload-code={nav.preloadCode}
		aria-labelledby={ariaLabelledBy}
	>
		<figure
			class="relative aspect-4/3 overflow-hidden bg-base-200"
			aria-label="Preview images from {collection.metadata.collectionTitle}"
		>
			<div class="grid h-full w-full grid-cols-2 grid-rows-2 gap-0.5">
				{#each collection.images as image, i (image.filename)}
					{const src = $derived<string>(UrlUtil.getFullImageUrl(image.filename))}
					<div class={mosaicClass(collection.images.length, i)}>
						<img
							{src}
							alt={image.altText}
							width={image.width}
							height={image.height}
							{loading}
							fetchpriority={fetchPriority}
							class="h-full w-full object-cover contrast-105 grayscale-45 transition-all duration-500 ease-out group-hover:scale-105 group-hover:grayscale-0"
						/>
					</div>
				{/each}
			</div>

			{#if collection.hasUnescoSite}
				<span class="absolute top-3 right-3 badge badge-soft font-mono text-xs badge-secondary"> INCLUDES UNESCO SITE </span>
			{/if}
		</figure>

		<div class="card-body flex grow flex-col p-5">
			<span class="font-mono text-xs font-light tracking-widest text-base-content/70">
				COLLECTION {plateNumber(index)}
			</span>
			<h2 id={ariaLabelledBy} class="card-title text-xl leading-snug font-medium text-base-content">
				{collection.metadata.collectionTitle}
			</h2>
			<p class="line-clamp-3 text-sm leading-relaxed text-base-content/70">
				{collection.metadata.description}
			</p>
			<div class="flex items-center justify-between border-t border-base-content/5 pt-4">
				<span class="font-mono text-xs font-light tracking-widest text-base-content/70">
					{collection.totalItems} places
				</span>
				<span class="mt-auto inline-flex items-center gap-1.5 font-mono text-sm font-semibold text-primary">
					explore collection
					<IconArrowRight class="size-4 text-base transition-transform duration-200 group-hover:translate-x-1" />
				</span>
			</div>
		</div>
	</a>
</div>
